import React from 'react';
import { keyframes } from '@emotion/react';
import LockRounded from '@mui/icons-material/LockRounded';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import type { InstitutionLogin } from 'app-components/flux/institution-logins/institutionLoginsTypes';
import { useFiMigrationPrompt } from 'app-components/hooks/accounts/useFiMigration';
import type { QIllustrationName } from 'app-components/assets/QIllustration/qIllustrationLoaders';
import { qknColors } from 'app-components/utils/theme';
import QIllustration from 'components/QIllustration';
import InstitutionIcon from 'components/InstitutionIcon';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';

const reveal = keyframes({
  from: { opacity: 0, transform: 'translateY(8px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});
const bankLogo = keyframes({
  from: { opacity: 0, transform: 'translateX(37px)' },
  to: { opacity: 1, transform: 'translateX(0)' },
});
const quickenLogo = keyframes({ from: { transform: 'translateX(-37px)' }, to: { transform: 'translateX(0)' } });
const useStyles = makeStyles()((theme) => ({
  root: {
    '& [data-reveal]': { animation: `${reveal} 300ms ease-out both` },
    '& [data-reveal="header"]': { animationDelay: '600ms', animationDuration: '600ms' },
    '& [data-reveal="how"]': { animationDelay: '1200ms' },
    '& [data-reveal="step-0"]': { animationDelay: '1500ms' },
    '& [data-reveal="step-1"]': { animationDelay: '1800ms' },
    '& [data-reveal="step-2"]': { animationDelay: '2100ms' },
    '& [data-logo="bank"]': { animation: `${bankLogo} 600ms ease-in-out both` },
    '& [data-logo="quicken"]': { animation: `${quickenLogo} 600ms ease-in-out both` },
    '@media (prefers-reduced-motion: reduce)': { '& [data-reveal], & [data-logo]': { animation: 'none' } },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 46,
    height: 46,
    borderRadius: theme.shape.radius.full,
    backgroundColor: qknColors.blue[600],
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: theme.shadows[1],
    zIndex: 1,
  },
}));

export default function ConnectionUpdateIntro({
  institutionLogin,
  onSubmit,
}: {
  institutionLogin: InstitutionLogin;
  onSubmit: () => void;
}): React.JSX.Element {
  const { classes } = useStyles();
  const isDark = useTheme().palette.mode === 'dark';
  const trackPrompt = useFiMigrationPrompt({ institutionLogin, surface: 'intro modal', location: 'account discovery' });
  const institutionName = institutionLogin.name || 'your financial institution';
  const steps: { icon: QIllustrationName; title: string; description: string }[] = [
    {
      icon: isDark ? 'fiMigrationBankDark' : 'fiMigrationBank',
      title: `Sign in to ${institutionName}`,
      description: "Enter your login credentials to confirm it's you.",
    },
    {
      icon: isDark ? 'fiMigrationAccountsDark' : 'fiMigrationAccounts',
      title: 'Select your accounts',
      description: 'Choose the same accounts you’ve already added.',
    },
    {
      icon: isDark ? 'fiMigrationDoneDark' : 'fiMigrationDone',
      title: 'That’s it',
      description: "You're reconnected and ready to go.",
    },
  ];

  return (
    <>
      <StdDialogContent className={classes.root} sx={{ px: { xs: 2, sm: 6 }, pt: 4, pb: 5, borderBottom: 0 }}>
        <Stack gap={3}>
          <Stack gap={1} alignItems='center'>
            <Stack direction='row' gap={0.5} alignItems='center' height={72} aria-hidden>
              <Box data-logo='bank'>
                <InstitutionIcon
                  institutionLoginId={institutionLogin.id}
                  sizePixels={46}
                  sx={{ borderColor: 'border.light', borderRadius: 'full', boxShadow: 1 }}
                />
              </Box>
              <Box sx={{ display: 'flex', transform: 'rotate(180deg)' }}>
                <QIllustration
                  name={isDark ? 'fiMigrationConnectionDark' : 'fiMigrationConnection'}
                  alt=''
                  width={20}
                  height={2}
                />
              </Box>
              <Box className={classes.logo} data-logo='quicken'>
                <QIllustration name='fiMigrationQuicken' alt='' width={29} height={26} />
              </Box>
            </Stack>
            <Stack
              textAlign='center'
              gap={1}
              data-reveal='header'
              sx={{ width: '100%', minWidth: 0, overflowWrap: 'anywhere' }}
            >
              <Typography variant='titleMedium'>An improved {institutionName} connection is ready</Typography>
              <Typography variant='paragraphRegular' color='text.secondary'>
                Updating only takes a minute - you won’t lose any account or transaction history.
              </Typography>
            </Stack>
          </Stack>
          <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: '2xl' }}>
            <Stack gap={3}>
              <Typography variant='bodyMedium' data-reveal='how'>
                How it works...
              </Typography>
              {steps.map((step, index) => (
                <Stack key={step.title} direction='row' alignItems='flex-start' gap={2} data-reveal={`step-${index}`}>
                  <Box sx={{ p: 0.5, bgcolor: 'primary.light', borderRadius: 'md', display: 'flex', flexShrink: 0 }}>
                    <QIllustration name={step.icon} alt='' width={20} height={20} />
                  </Box>
                  <Stack gap={0.25} sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                    <Typography variant='paragraphMedium'>{step.title}</Typography>
                    <Typography variant='captionRegular' color='text.secondary'>
                      {step.description}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </StdDialogContent>
      <StdDialogActions
        // The Figma footer's spacing includes its one-pixel top border.
        sx={{ px: { xs: 2, sm: 3 }, pb: (theme) => `calc(${theme.spacing(3)} - 1px)` }}
        tertiaryNode={
          <Stack direction='row' alignItems='center' gap={1} flex={1} minWidth={0} mr={2}>
            <LockRounded sx={{ fontSize: 16, color: 'icon.light', flexShrink: 0 }} />
            <Typography
              variant='paragraphRegular'
              color='text.secondary'
              sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
            >
              Safe and secure with Quicken
            </Typography>
          </Stack>
        }
        primary={{
          children: 'Let’s go',
          // Include the button border in Figma's horizontal inset.
          sx: { px: (theme) => `calc(${theme.spacing(2)} - 1px)`, minWidth: 85 },
          onClick: () => {
            trackPrompt('clicked');
            onSubmit();
          },
        }}
      />
    </>
  );
}
